import { useState, useEffect, useCallback } from 'react';
import { INITIAL_FORM_STATE } from '../constants/returnRequestConstants';
import { validateReturnRequestForm, isFormValid } from '../utils/returnRequestValidation';

/**
 * İade talebi formu için custom hook
 * @param {Object} selectedPart - Seçili parça
 * @param {boolean} visible - Dialog görünürlüğü
 * @param {Function} th - Translation fonksiyonu
 * @param {boolean} requireInvoiceNo - Fatura No zorunlu mu (yurt dışı bayi)
 * @returns {Object} - Form state ve fonksiyonları
 */
const useReturnRequestForm = (selectedPart, visible, th, requireInvoiceNo = false) => {
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);

    /**
     * Form alanını güncelle
     */
    const handleInputChange = useCallback(
        (field, value) => {
            setFormData((prev) => ({
                ...prev,
                [field]: value
            }));

            // Kullanıcı yazmaya başladığında hatayı temizle
            if (errors[field]) {
                setErrors((prev) => ({
                    ...prev,
                    [field]: null
                }));
            }
        },
        [errors]
    );

    /**
     * Parça seçimini güncelle
     */
    const handlePartSelection = useCallback(
        (partCode, partOptions) => {
            if (partCode == null) {
                setFormData((prev) => ({
                    ...prev,
                    partCode: '',
                    uom: ''
                }));
            } else {
                const selectedPartInfo = partOptions.find((p) => p.code === partCode);
                if (selectedPartInfo) {
                    setFormData((prev) => ({
                        ...prev,
                        partCode: selectedPartInfo.code,
                        uom: selectedPartInfo.uom
                    }));
                }
            }

            if (errors.part) {
                setErrors((prev) => ({ ...prev, part: null }));
            }
        },
        [errors.part]
    );

    /**
     * Form validasyonu
     */
    const validateForm = useCallback(() => {
        const newErrors = validateReturnRequestForm(formData, th, { requireInvoiceNo });
        setErrors(newErrors);
        return isFormValid(newErrors);
    }, [formData, th, requireInvoiceNo]);

    /**
     * Form'u sıfırla
     */
    const resetForm = useCallback(() => {
        if (selectedPart?.id) {
            // Mevcut parça seçiliyse
            setFormData({
                ...INITIAL_FORM_STATE,
                partCode: selectedPart.partCode || '',
                uom: selectedPart.uom || ''
            });
        } else {
            // Yeni iade talebi
            setFormData(INITIAL_FORM_STATE);
        }
        setFiles([]);
        setErrors({});
        setSubmitted(false);
    }, [selectedPart]);

    /**
     * Dialog açıldığında form'u sıfırla
     */
    useEffect(() => {
        if (visible) {
            resetForm();
        }
    }, [visible, resetForm]);

    return {
        formData,
        files,
        errors,
        submitted,
        setSubmitted,
        setFiles,
        handleInputChange,
        handlePartSelection,
        validateForm,
        resetForm
    };
};

export default useReturnRequestForm;
